import { useMount } from '@contra/react-hooks/useMount';
import { useCallback, useRef, useState } from 'react';

type InteractionTimerOptions = {
  timeoutMs?: number;
};

type InteractionTimerTuple = [
  isInteracting: boolean,
  initiateInteraction: () => void,
];

/**
 * Helper hook for initiating a timer when initiateInteraction is called. This allows you to easily
 * make temporary UI changes when the user interacts with a DOM element. See ToolsInput.tsx for example.
 *
 * How it works:
 * 1) initiateInteraction is called–this indicates that a new user interaction has occurred
 * 2) The hook clears the timeout, if there's currently one initialized
 * 3) The hook sets isInteracting to true for the consumer to check
 * 4) The hook sets a timeout for the specified timeoutMs (defaults to 1_000ms)
 * 5) InitiateInteraction and isInteracting are tracked separately so every time the user interacts,
 * it ensures that the timer is reset and does not pulse between interacting/not interacting.
 */
export const useInteractionTimer = ({
  timeoutMs = 1_000,
}: InteractionTimerOptions = {}): InteractionTimerTuple => {
  const [isInteracting, setIsInteracting] = useState<boolean>(false);
  const interactionTimerRef = useRef<null | number>(null);

  const initiateInteraction = useCallback(() => {
    // cleanup current timeout if there is one!
    if (interactionTimerRef.current) {
      window.clearTimeout(interactionTimerRef.current);
    }

    interactionTimerRef.current = window.setTimeout(() => {
      // when the timer is up, set interacting to false
      setIsInteracting(false);
    }, timeoutMs);
    setIsInteracting(true);
  }, [timeoutMs]);

  useMount(() => {
    return () => {
      if (window && interactionTimerRef.current) {
        // Just make absolutely sure that the timeouts are cleaned up!
        window.clearTimeout(interactionTimerRef.current);
      }
    };
  });

  return [isInteracting, initiateInteraction];
};
